import { useCallback, useEffect, useState } from 'react';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';

import Divider from '@mui/material/Divider';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';

import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MemoryIcon from '@mui/icons-material/Memory';

import LegendToggle from '@mui/icons-material/LegendToggle';

import AbcIcon from '@mui/icons-material/Abc';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import IconButton from '@mui/material/IconButton';

import { Drawer } from './AppBar';
import BullsScreen from '../screens/BullsScreen';
import OrdersScreen from '../screens/OrdersScreen';
import VariablesScreen from '../screens/VariablesScreen';
import DashboardScreen from '../screens/DashboardScreen';
import LogoutScreen from '../screens/LogoutScreen';
import LoginScreen from '../screens/LoginScreen';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';
import { Env } from '../interfaces/globaleInterface';
import { getCurrentToken, getCurrentEnv } from '../services/ApiServices';
import AdministrationScreen from '../screens/AdministrationScreen';
import { UserDTO } from '../models/userDTO';
import { frFR } from '@mui/x-data-grid/locales';
import { frFR as pickersFrFR } from '@mui/x-date-pickers/locales';
import { frFR as coreFrFR } from '@mui/material/locale';
import { unauthorizedEventName } from '../interfaces/networkManager';

const grafanaUrl = 'http://51.91.21.219:3000/';

const getStoredUser = (): UserDTO => {
	const user = localStorage.getItem('user');
	if (!user) return {} as UserDTO;

	try {
		return JSON.parse(user);
	} catch {
		localStorage.removeItem('user');
		return {} as UserDTO;
	}
};

export default function WrapperContent() {
	const [isConnected, setIsConnected] = useState(getCurrentToken() !== '');
	const [open, setOpen] = useState(false);
	const [currentPage, setCurrentPage] = useState('Dashboard');
	const [env, setEnv] = useState<Env>(getCurrentEnv() || Env.DEV);
	const [userCurrent, setUserCurrent] = useState<UserDTO>({} as UserDTO);
	const mdTheme = createTheme(
		{
			palette: {
				primary: {
					main: '#1976d2',
				},
			},
		},
		frFR,
		pickersFrFR,
		coreFrFR
	);

	const disconnect = useCallback(() => {
		localStorage.removeItem('token');
		localStorage.removeItem('user');
		setCurrentPage('Dashboard');
		setIsConnected(false);
		setOpen(false);
	}, []);

	useEffect(() => {
		const user = getStoredUser();
		setUserCurrent(user);
		if (isConnected) {
			user.extended_role === 'PHF_SIEGE_ADMIN' && setCurrentPage('Administration');
		}
	}, [isConnected]);

	useEffect(() => {
		window.addEventListener(unauthorizedEventName, disconnect);
		return () => window.removeEventListener(unauthorizedEventName, disconnect);
	}, [disconnect]);

	const toggleDrawer = () => {
		setOpen(!open);
	};

	const changeEnvCallback = (newEnv: Env) => {
		localStorage.setItem('env', newEnv);
		setEnv(newEnv);
	};
	return (
		<ThemeProvider theme={mdTheme}>
			<Box sx={{ display: 'flex' }}>
				<CssBaseline />
				<Header isConnected={isConnected} toolbarIsOpen={open} env={env} toggleDrawer={toggleDrawer} />
				<Drawer variant="permanent" open={open}>
					<Toolbar
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							px: [1],
						}}
					>
						{isConnected && (
							<IconButton
								onClick={() => {
									toggleDrawer();
								}}
							>
								<ChevronLeftIcon />
							</IconButton>
						)}
					</Toolbar>
					<Divider />
					{isConnected && (
						<List>
							{userCurrent?.extended_role !== 'PHF_SIEGE_ADMIN' && (
								<>
									<ListItemButton onClick={() => setCurrentPage('Dashboard')}>
										<ListItemIcon>
											<DashboardIcon />
										</ListItemIcon>
										<ListItemText primary="Dashboard" />
									</ListItemButton>
									<ListItemButton onClick={() => setCurrentPage('Commandes')}>
										<ListItemIcon>
											<MemoryIcon />
										</ListItemIcon>
										<ListItemText primary="Commandes" />
									</ListItemButton>
									<ListItemButton onClick={() => setCurrentPage('Bulls')}>
										<ListItemIcon>
											<AgricultureIcon />
										</ListItemIcon>
										<ListItemText primary="Bulls" />
									</ListItemButton>
									<ListItemButton onClick={() => setCurrentPage('Variables')}>
										<ListItemIcon>
											<AbcIcon />
										</ListItemIcon>
										<ListItemText primary="Variables" />
									</ListItemButton>
									<ListItemButton onClick={() => window.open(grafanaUrl, '_blank')}>
										<ListItemIcon>
											<LegendToggle />
										</ListItemIcon>
										<ListItemText primary="Grafana" />
									</ListItemButton>
								</>
							)}
							<ListItemButton onClick={() => setCurrentPage('Administration')}>
								<ListItemIcon>
									<SupervisorAccountIcon />
								</ListItemIcon>
								<ListItemText primary="Administration" />
							</ListItemButton>
							<ListItemButton onClick={() => setCurrentPage('Logout')}>
								<ListItemIcon>
									<SentimentDissatisfiedIcon />
								</ListItemIcon>
								<ListItemText primary="Déconnexion" />
							</ListItemButton>
						</List>
					)}
				</Drawer>

				<Box
					component="main"
					sx={{
						backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
						flexGrow: 1,
						height: '100vh',
						overflow: 'auto',
					}}
				>
					<Toolbar />
					<Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
						<Grid container sx={{ spacing: 0 }}>
							{isConnected ? (
								currentPage === 'Dashboard' ? (
									<DashboardScreen />
								) : currentPage === 'Commandes' ? (
									<OrdersScreen />
								) : currentPage === 'Variables' ? (
									<VariablesScreen />
								) : currentPage === 'Bulls' ? (
									<BullsScreen />
								) : currentPage === 'Administration' ? (
									<AdministrationScreen user={userCurrent} />
								) : currentPage === 'Logout' ? (
									<LogoutScreen disconnect={disconnect} />
								) : null
							) : (
								<LoginScreen
									changeEnvCallback={changeEnvCallback}
									successCallBack={(env: Env, token: string) => {
										setEnv(env);
										setIsConnected(true);
									}}
								/>
							)}
						</Grid>
						<Footer sx={{ pt: 4 }} />
					</Container>
				</Box>
			</Box>
		</ThemeProvider>
	);
}
